import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, CardHeader, Media } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faComments, faImage, faStore } from '@fortawesome/free-solid-svg-icons'
import Loader from '../../common/Loader'
import Pagination from '../../common/pagination/Pagination'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import { getServiceRequests } from '../../../helpers/api/marketplace'
import { storageUrl } from '../marketplaceConstants'
import { REQUEST_FILTERS, REQUEST_STATUS, formatMessageTime, lastMessagePreview } from './requestConstants'

const RequestRow = ({ item, side, basePath }) => {
  const status = REQUEST_STATUS[item.status] || REQUEST_STATUS.pending
  const counterpart =
    side === 'provider'
      ? [item.owner?.name, item.property?.property && `inmueble ${item.property.property}`].filter(Boolean).join(' · ')
      : item.provider?.display_name
  const cover = storageUrl(item.service?.cover)
  const lastAt = item.last_message?.created_at || item.created_at

  return (
    <Link to={`${basePath}/${item.id}`} className="d-block text-decoration-none border-bottom px-3 py-3 hover-bg-100">
      <Media className="align-items-center">
        <div className="rounded overflow-hidden bg-200 mr-3 flex-shrink-0 d-flex align-items-center justify-content-center text-400" style={{ width: 56, height: 56 }}>
          {cover ? <img src={cover} alt="" className="w-100 h-100" style={{ objectFit: 'cover' }} /> : <FontAwesomeIcon icon={faImage} />}
        </div>
        <Media body className="overflow-hidden">
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 6 }}>
            <h6 className="mb-0 text-truncate text-900">{item.service?.title}</h6>
            <Badge color={`soft-${status.color}`} pill>
              {status.label}
            </Badge>
          </div>
          <p className="fs--1 text-700 mb-0 text-truncate">{counterpart}</p>
          <p className={`fs--1 mb-0 text-truncate ${item.unread ? 'text-900 font-weight-semi-bold' : 'text-600'}`}>
            {item.last_message?.sender_role === side && item.last_message?.type === 'text' ? 'Tú: ' : ''}
            {lastMessagePreview(item.last_message)}
          </p>
        </Media>
        <div className="text-right ml-3 flex-shrink-0">
          <p className="fs--2 text-600 mb-1">{formatMessageTime(lastAt)}</p>
          {item.unread > 0 && (
            <Badge color="primary" pill>
              {item.unread}
            </Badge>
          )}
        </div>
      </Media>
    </Link>
  )
}

const ServiceRequestsList = ({ side, basePath, emptyAction }) => {
  const [filter, setFilter] = useState('open')
  const [page, setPage] = useState(1)
  const query = new URLSearchParams({ page: String(page), limit: '20', ...(filter ? { status: filter } : {}) }).toString()
  const requests = useSWRFetch(['marketplace-requests', query], (key, value) => getServiceRequests(value), { refreshInterval: 15000 })

  const items = requests.data?.data || []
  const meta = requests.data?.meta

  const changeFilter = (value) => {
    setFilter(value)
    setPage(1)
  }

  return (
    <Card className="mb-3 mt-1 overflow-hidden">
      <CardHeader className="bg-light d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
        <div className="mr-auto">
          <h5 className="mb-1">{side === 'provider' ? 'Solicitudes de propietarios' : 'Mis solicitudes'}</h5>
          <p className="fs--1 text-600 mb-0">
            {side === 'provider'
              ? 'Pedidos de visita y cotización que te enviaron desde el Marketplace.'
              : 'Tus pedidos de visita y cotización a los proveedores del Marketplace.'}
          </p>
        </div>
        <ButtonGroup size="sm">
          {REQUEST_FILTERS.map((item) => (
            <Button key={item.value} color={filter === item.value ? 'primary' : 'falcon-default'} onClick={() => changeFilter(item.value)}>
              {item.label}
            </Button>
          ))}
        </ButtonGroup>
      </CardHeader>

      {requests.error ? (
        <CardBody>
          <Alert color="danger" className="mb-0">
            No pudimos cargar las solicitudes.
            <Button color="link" size="sm" className="ml-1 p-0 align-baseline" onClick={() => requests.mutate()}>
              Reintentar
            </Button>
          </Alert>
        </CardBody>
      ) : requests.isLoading ? (
        <Loader padding="py-5" />
      ) : items.length === 0 ? (
        <CardBody className="text-center py-5">
          <FontAwesomeIcon icon={side === 'provider' ? faComments : faStore} size="3x" className="text-300 mb-3" />
          <p className="text-600 mb-3">
            {side === 'provider'
              ? 'Todavía no tienes solicitudes en este estado. Cuando un propietario pida una visita o una cotización, aparecerá aquí.'
              : 'No tienes solicitudes en este estado.'}
          </p>
          {emptyAction}
        </CardBody>
      ) : (
        <>
          {items.map((item) => (
            <RequestRow key={item.id} item={item} side={side} basePath={basePath} />
          ))}
          {meta && meta.last_page > 1 && (
            <CardBody className="d-flex justify-content-center py-3">
              <Pagination
                meta={{ currentPage: meta.current_page, lastPage: meta.last_page }}
                prevPage={() => setPage(meta.current_page - 1)}
                nextPage={() => setPage(meta.current_page + 1)}
                goToPage={setPage}
              />
            </CardBody>
          )}
        </>
      )}
    </Card>
  )
}

export default ServiceRequestsList
